import type { PaginaExtra } from '@miolos/core';
import {
  DESCRICAO_PAGINA_EXTRA,
  PAGINAS_EXTRAS,
  ROTULO_PAGINA_EXTRA,
} from '@miolos/core';
import { Campo } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/input';
import { LinhaSwitch } from '@/components/ui/switch';
import { useEditor } from '../EstadoEditor';
import { Grade, TituloPasso } from '../ui';
import { CalendariosExtras } from './CalendariosExtras';

/**
 * Extras que não são calendário.
 *
 * A visão do ano e as divisórias de mês saem daqui: elas vivem na seção
 * “Calendários extras”, junto do mini calendário do mês e dos feriados.
 */
const EXTRAS_SEM_CALENDARIO = PAGINAS_EXTRAS.filter(
  (extra): extra is PaginaExtra => extra !== 'ANO' && extra !== 'MESES',
);

/* ------------------------------------------------------------------ */
/* 9. Dados pessoais                                                   */
/* ------------------------------------------------------------------ */

export function PassoDadosPessoais() {
  const { config, definirDados } = useEditor();
  const dados = config.dadosPessoais;

  return (
    <div className="space-y-5">
      <TituloPasso
        titulo="Dados do proprietário"
        descricao="Essas informações aparecem na capa e na página “Meus dados”. É o que faz uma agenda perdida voltar para as mãos certas."
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo rotulo="Nome completo">
          <Input value={dados.nome} onChange={(e) => definirDados({ nome: e.target.value })} placeholder="Como você quer que apareça na capa" />
        </Campo>
        <Campo rotulo="Data de nascimento" dica="Sai impressa como guia: __/__/______">
          <Input
            value={dados.nascimento}
            onChange={(e) => definirDados({ nascimento: e.target.value })}
            placeholder="12/04/1985"
          />
        </Campo>
        <Campo rotulo="Telefone / WhatsApp" dica="A página imprime a máscara (00) 00000-0000.">
          <Input value={dados.telefone} onChange={(e) => definirDados({ telefone: e.target.value })} placeholder="(00) 00000-0000" />
        </Campo>
        <Campo rotulo="E-mail">
          <Input type="email" value={dados.email} onChange={(e) => definirDados({ email: e.target.value })} placeholder="voce@email.com" />
        </Campo>
        <Campo rotulo="Endereço">
          <Input
            value={dados.endereco}
            onChange={(e) => definirDados({ endereco: e.target.value })}
            placeholder="Rua, número e complemento"
          />
        </Campo>
        <Campo rotulo="Cidade / UF">
          <Input value={dados.cidade} onChange={(e) => definirDados({ cidade: e.target.value })} placeholder="Cidade, UF" />
        </Campo>
        <Campo rotulo="CEP" dica="A página imprime a máscara 00000-000.">
          <Input value={dados.cep} onChange={(e) => definirDados({ cep: e.target.value })} placeholder="00000-000" />
        </Campo>
      </div>

      <h3 className="pt-1 text-sm font-semibold">Contato de emergência</h3>

      <div className="grid gap-4 sm:grid-cols-3">
        <Campo rotulo="Nome">
          <Input
            value={dados.contatoEmergencia}
            onChange={(e) => definirDados({ contatoEmergencia: e.target.value })}
            placeholder="Nome de quem avisar"
          />
        </Campo>
        <Campo rotulo="Parentesco ou relação">
          <Input
            value={dados.relacaoEmergencia}
            onChange={(e) => definirDados({ relacaoEmergencia: e.target.value })}
            placeholder="Mãe, cônjuge, amiga..."
          />
        </Campo>
        <Campo rotulo="Telefone" dica="Com máscara (00) 00000-0000.">
          <Input
            value={dados.telefoneEmergencia}
            onChange={(e) => definirDados({ telefoneEmergencia: e.target.value })}
            placeholder="(00) 00000-0000"
          />
        </Campo>
      </div>

      <Campo
        rotulo="Recado para quem encontrar a agenda"
        dica="Deixe em branco para usar o texto padrão."
      >
        <Textarea
          value={dados.avisoPerda}
          onChange={(e) => definirDados({ avisoPerda: e.target.value })}
          rows={3}
          placeholder="Ex.: Esta agenda é importante para mim. Por favor, me avise pelo telefone acima e combinamos a devolução."
        />
      </Campo>

      <LinhaSwitch
        id="mostrar-capa"
        rotulo="Mostrar o nome na capa"
        descricao="Exibe um quadro com nome, telefone e e-mail sobre a capa."
        checked={dados.mostrarNaCapa}
        onCheckedChange={(valor) => definirDados({ mostrarNaCapa: valor })}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 10. Páginas extras                                                  */
/* ------------------------------------------------------------------ */

export function PassoExtras() {
  const { config, alternarExtra, atualizar, documento } = useEditor();

  return (
    <div className="space-y-5">
      <TituloPasso
        titulo="Páginas extras do miolo"
        descricao="Tudo opcional. Desmarque o que não vai usar para deixar a agenda mais enxuta e barata de imprimir."
      />

      <LinhaSwitch
        id="incluir-capa"
        rotulo="Incluir a capa gerada pelo sistema"
        descricao="Desligada por padrão: quem encaderna costuma fazer a capa por fora. Se você enviar uma arte de capa no passo Imagens, ela entra sozinha — com capa gerada ou sem."
        checked={config.capa}
        onCheckedChange={(valor) => atualizar({ capa: valor })}
      />

      {!config.capa ? (
        <p className="rounded-md border border-border bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground">
          O miolo começa direto nas páginas de conteúdo. As imagens marcadas para aparecer
          <strong className="text-foreground"> somente na capa</strong> ficam guardadas para quando você enviar uma
          arte de capa no passo <strong className="text-foreground">Imagens</strong>.
        </p>
      ) : null}

      <Grade colunas={2}>
        {EXTRAS_SEM_CALENDARIO.map((extra) => {
          const ativo = config.paginasExtras.includes(extra);
          return (
            <button
              key={extra}
              type="button"
              onClick={() => alternarExtra(extra)}
              aria-pressed={ativo}
              className={[
                'flex items-start gap-3 rounded-lg border p-3 text-left transition-colors',
                ativo ? 'border-primary bg-primary/[0.04]' : 'border-border bg-card hover:border-primary/40',
              ].join(' ')}
            >
              <span
                className={[
                  'mt-0.5 flex size-4 shrink-0 items-center justify-center rounded border',
                  ativo ? 'border-primary bg-primary text-primary-foreground' : 'border-input',
                ].join(' ')}
              >
                {ativo ? (
                  <svg viewBox="0 0 12 12" className="size-3" fill="none" stroke="currentColor" strokeWidth="2.4">
                    <path d="M2 6.4 L4.6 9 L10 3" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : null}
              </span>
              <span className="space-y-0.5">
                <span className="block text-sm font-medium leading-tight">{ROTULO_PAGINA_EXTRA[extra]}</span>
                <span className="block text-xs leading-relaxed text-muted-foreground">
                  {DESCRICAO_PAGINA_EXTRA[extra]}
                </span>
              </span>
            </button>
          );
        })}
      </Grade>

      <p className="rounded-md border border-border bg-muted/50 p-3 text-xs leading-relaxed text-muted-foreground">
        Páginas de contatos, senhas, anotações e cadernos temáticos agora ficam no passo <strong className="text-foreground">Cadernos</strong>, onde você escolhe o tipo e a quantidade de páginas.
      </p>

      {/* Visão do ano, mini calendário do mês e feriados, todos no mesmo lugar */}
      <CalendariosExtras />

      <LinhaSwitch
        id="numerar"
        rotulo="Numerar as páginas"
        descricao="Imprime o número no rodapé — essencial se você usa o índice."
        checked={config.numerarPaginas}
        onCheckedChange={(valor) => atualizar({ numerarPaginas: valor })}
      />

      {config.paginasExtras.includes('INDICE') && !config.numerarPaginas ? (
        <p className="rounded-md border border-amber-300/70 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
          O índice usa os números de página. Com a numeração desligada ele fica decorativo.
        </p>
      ) : null}

      <p className="text-xs text-muted-foreground">
        Com a seleção atual o miolo tem <strong className="text-foreground">{documento.resumo.totalPaginas}</strong>{' '}
        páginas.
      </p>
    </div>
  );
}
